/**
 * Facebook Feed Post Mockup
 * Simulates how an ad appears in the Facebook feed
 */

import React from 'react';
import { Globe, MoreHorizontal, ThumbsUp, MessageCircle, Share2 } from 'lucide-react';

interface FacebookFeedMockupProps {
  children: React.ReactNode;
  adWidth: number;
  adHeight: number;
}

export function FacebookFeedMockup({ children, adWidth, adHeight }: FacebookFeedMockupProps) {
  return (
    <div className="bg-white rounded-lg overflow-hidden shadow-lg w-full max-w-[500px]">
      {/* Post header */}
      <div className="p-3 flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
          <span className="text-white font-bold text-sm">M</span>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-1">
            <span className="font-semibold text-[#050505] text-sm">Mooncloak</span>
            <svg className="w-3.5 h-3.5 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <div className="flex items-center gap-1 text-xs text-[#65676B]">
            <span>Sponsored</span>
            <span>·</span>
            <Globe className="w-3 h-3" />
          </div>
        </div>
        <button className="p-2 hover:bg-gray-100 rounded-full">
          <MoreHorizontal className="w-5 h-5 text-[#65676B]" />
        </button>
      </div>

      {/* Post text */}
      <div className="px-3 pb-2">
        <p className="text-[#050505] text-sm">
          Protect your privacy online. Start your free trial today.
        </p>
      </div>

      {/* Ad image */}
      <div className="w-full">
        {children}
      </div>

      {/* Link preview bar */}
      <div className="px-3 py-2 bg-[#F0F2F5] border-t border-[#CED0D4]">
        <div className="text-xs text-[#65676B] uppercase tracking-wide">mooncloak.com</div>
        <div className="text-sm font-semibold text-[#050505]">Your Privacy, Protected</div>
        <div className="text-xs text-[#65676B]">Take control of your digital footprint</div>
      </div>

      {/* Engagement stats */}
      <div className="px-3 py-2 flex items-center justify-between border-t border-[#CED0D4]">
        <div className="flex items-center gap-1">
          <div className="flex -space-x-1">
            <div className="w-[18px] h-[18px] rounded-full bg-[#1877F2] flex items-center justify-center">
              <ThumbsUp className="w-2.5 h-2.5 text-white" fill="white" />
            </div>
            <div className="w-[18px] h-[18px] rounded-full bg-red-500 flex items-center justify-center text-[10px]">
              ❤️
            </div>
          </div>
          <span className="text-xs text-[#65676B] ml-1">2.4K</span>
        </div>
        <div className="text-xs text-[#65676B]">
          156 comments · 89 shares
        </div>
      </div>

      {/* Action buttons */}
      <div className="px-3 py-1 flex items-center border-t border-[#CED0D4]">
        <button className="flex-1 flex items-center justify-center gap-2 py-2 hover:bg-[#F0F2F5] rounded-md">
          <ThumbsUp className="w-5 h-5 text-[#65676B]" />
          <span className="text-sm font-semibold text-[#65676B]">Like</span>
        </button>
        <button className="flex-1 flex items-center justify-center gap-2 py-2 hover:bg-[#F0F2F5] rounded-md">
          <MessageCircle className="w-5 h-5 text-[#65676B]" />
          <span className="text-sm font-semibold text-[#65676B]">Comment</span>
        </button>
        <button className="flex-1 flex items-center justify-center gap-2 py-2 hover:bg-[#F0F2F5] rounded-md">
          <Share2 className="w-5 h-5 text-[#65676B]" />
          <span className="text-sm font-semibold text-[#65676B]">Share</span>
        </button>
      </div>
    </div>
  );
}
